import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Link,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Drawer,
  ListItemButton,
  CircularProgress,
  TextField,
  Tooltip,
  Paper,
  ListItemText,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  MenuItem,
  Menu,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  ListItemIcon,
  Switch,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import DetailDialog from "../components/LinkDetailsDialog";
import TechStackChipList from "../components/TechStackChipList";

import TimezoneUtils from '../utils/timezoneUtils';
import {
  EXTERNAL_LINK_REL,
  openExternalUrl,
  stripTrackingParams,
} from '../utils/externalUrlUtils';
import {
  parseGoogleResultDate,
  formatLinkPostedDate,
} from '../utils/googleDateUtils';
import "react-toastify/dist/ReactToastify.css";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SegmentIcon from "@mui/icons-material/Segment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Slider from "@mui/material/Slider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../context/AuthContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
const OPENED_LINKS_STORAGE_KEY = "openedBidLinks";
const OTHER_CATEGORY = "Other";

const getLinkDisplayCategory = (link, teamCategories) => {
  const category = link.queryId?.category;
  if (!category) return OTHER_CATEGORY;
  if (
    teamCategories !== null &&
    teamCategories.length > 0 &&
    !teamCategories.includes(category)
  ) {
    return OTHER_CATEGORY;
  }
  return category;
};
const MAX_STORED_LINKS = 10000;
const LINK_EXPIRY_DAYS = 30;
const STRICT_TAGS = [
  "All",
  "Email Found",
  "Remote Job",
  "Remote/Hybrid",
  "Non Remote Job",
  "Other Relevant",
  "Login Required",
  "Verification Required",
  "Expired",
  "Irrelevant",
  "Uncategorized",
];
const TAG_PRIORITY = {
  "Email Found": 1,
  "Remote Job": 2,
  "Remote/Hybrid": 3,
  "Non Remote Job": 4,
  "Login Required": 5,
  "Other Relevant": 6,
  "Verification Required": 7,
  Expired: 8,
  Irrelevant: 9,
  Uncategorized: 10,
};

const createDefaultVisibleTags = () =>
  STRICT_TAGS.reduce((acc, tag) => {
    acc[tag] = tag === "All";
    return acc;
  }, {});

const normalizeVisibleTags = (stored) => {
  const defaults = createDefaultVisibleTags();
  if (!stored || typeof stored !== "object") {
    return defaults;
  }
  return { ...defaults, ...stored };
};

const getBehaviorFilterLabel = (tag) => {
  if (tag === "Email Found") return "Email";
  if (tag === "Verification Required") return "Captcha Check";
  return tag;
};

const STANDARD_LOCATIONS = [
  "USA",
  "Canada",
  "LATAM",
  "Europe",
  "Asia",
  "Australia/Pacific",
  "Australia",
  "Africa",
  "Anonymous",
];

const getLinkLocation = (link) =>
  link?.location || link?.final_details?.jobContext?.location || "Unknown";

const getLinkTechStack = (link) =>
  link?.techStack || link?.final_details?.jobContext?.techStack || "N/A";

const getLinkTechStackList = (link) => {
  if (Array.isArray(link?.techStackList) && link.techStackList.length > 0) {
    return link.techStackList;
  }
  if (
    Array.isArray(link?.final_details?.jobContext?.techStackList) &&
    link.final_details.jobContext.techStackList.length > 0
  ) {
    return link.final_details.jobContext.techStackList;
  }
  if (
    Array.isArray(link?.final_details?.customizationTarget?.techStackList) &&
    link.final_details.customizationTarget.techStackList.length > 0
  ) {
    return link.final_details.customizationTarget.techStackList;
  }
  return [];
};

const renderLinkTechStacks = (link) => (
  <TechStackChipList techStackList={getLinkTechStackList(link)} />
);

const parseLinkPostedDate = (link) => parseGoogleResultDate(link?.date);

const matchesPostedDateTimeLimit = (link, limit) => {
  if (limit === -1) return true;

  const postedDate = parseLinkPostedDate(link);

  if (limit === 0) {
    return postedDate === null;
  }

  if (postedDate === null) return false;

  const now = new Date();
  const daysDiff = Math.floor(
    (now.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  return daysDiff <= limit;
};

const matchesPostedDateTimeRanges = (link, timeRange) => {
  const timeLimits = Array.isArray(timeRange) ? timeRange : [timeRange];
  if (timeLimits.length === 0) return true;
  return timeLimits.some((limit) => matchesPostedDateTimeLimit(link, limit));
};

const getPostedDateTextSx = (link) => {
  const postedDate = parseLinkPostedDate(link);
  if (postedDate === null) {
    return {};
  }

  const now = new Date();
  const daysDiff = Math.floor(
    (now.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysDiff <= 7) {
    return {
      color: (theme) => (theme.palette.mode === "dark" ? "#ffcc80" : "#ef6c00"),
      fontWeight: 600,
    };
  }

  if (daysDiff <= 30) {
    return {
      color: (theme) => (theme.palette.mode === "dark" ? "#81c784" : "#2e7d32"),
      fontWeight: 600,
    };
  }

  return {};
};

const DEFAULT_JOBSITE_KEYWORDS = [
  "greenhouse.io",
  "workable.com",
  "lever.co",
  "ashbyhq.com",
  "gem.com",
  "applytojob.com",
];

const normalizeJobsiteKeywords = (keywords) => {
  if (Array.isArray(keywords) && keywords.length > 0) {
    return keywords.map((keyword) => String(keyword).trim().toLowerCase()).filter(Boolean);
  }
  return DEFAULT_JOBSITE_KEYWORDS;
};

const escapeJobsiteRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const keywordMatchesJobsiteUrl = (rawUrl, keyword) => {
  if (!rawUrl || !keyword) return false;

  const lowerUrl = rawUrl.toLowerCase();
  const trimmedKeyword = keyword.trim().toLowerCase();
  if (!trimmedKeyword) return false;

  if (trimmedKeyword.includes("*")) {
    const parts = trimmedKeyword.split("*").map((part) => escapeJobsiteRegex(part));
    return new RegExp(parts.join("[^/]*"), "i").test(lowerUrl);
  }

  return lowerUrl.includes(trimmedKeyword);
};

const isUrlJobsite = (rawUrl = "", keywords = DEFAULT_JOBSITE_KEYWORDS) => {
  if (!rawUrl) return false;
  return keywords.some((keyword) => keywordMatchesJobsiteUrl(rawUrl, keyword));
};

const isJobsiteLink = (link, keywords = DEFAULT_JOBSITE_KEYWORDS) => {
  const urls = [link?.url, link?.final_details?.finalUrl].filter(Boolean);
  return urls.some((url) => isUrlJobsite(url, keywords));
};

const getOpenedLinks = () => {
  try {
    const stored = localStorage.getItem(OPENED_LINKS_STORAGE_KEY);
    if (!stored) return [];

    const links = JSON.parse(stored).map((link) => ({
      ...link,
      url: stripTrackingParams(link.url),
    }));
    const now = new Date().getTime();

    // Filter out expired links
    const validLinks = links.filter((link) => {
      const expiryDate =
        new Date(link.timestamp).getTime() +
        LINK_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      return now < expiryDate;
    });

    // If we filtered out any expired links, update storage
    if (validLinks.length !== links.length) {
      localStorage.setItem(
        OPENED_LINKS_STORAGE_KEY,
        JSON.stringify(validLinks)
      );
    }

    return validLinks;
  } catch (error) {
    console.error("Error reading opened links:", error);
    return [];
  }
};

const addOpenedLinks = (newLinks) => {
  const currentLinks = getOpenedLinks();
  try {
    const now = new Date().getTime();

    // Add new links with timestamp
    const linksToAdd = newLinks.map((url) => ({
      url: stripTrackingParams(url),
      timestamp: now,
    }));

    // Combine with existing links and limit to MAX_STORED_LINKS
    const updatedLinks = [...linksToAdd, ...currentLinks].slice(
      0,
      MAX_STORED_LINKS
    );

    localStorage.setItem(
      OPENED_LINKS_STORAGE_KEY,
      JSON.stringify(updatedLinks)
    );
    return updatedLinks;
  } catch (error) {
    console.error("Error saving opened links:", error);
    return currentLinks;
  }
};

const ConfidenceIndicator = React.memo(({ confidence }) => {
  // Convert confidence from 0-1 to 0-100
  const score = Math.round((confidence || 0) * 100);

  // Color gradient with theme-aware colors
  const getColor = (score, theme) => {
    const isDarkMode = theme?.palette?.mode === "dark";

    if (score < 40) {
      return isDarkMode ? "#ff5252" : "hsl(0, 100%, 50%)"; // Red - brighter in dark mode
    } else if (score < 80) {
      return isDarkMode ? "#ffab2e" : "hsl(60, 100.00%, 30.90%)"; // Yellow - brighter in dark mode
    }
    return isDarkMode ? "#2cdb92" : "hsl(120, 100%, 40%)"; // Green - brighter in dark mode
  };

  return (
    <Tooltip title={`Recommended: ${score}%`}>
      <Box
        sx={{
          display: "inline-flex",
          position: "relative",
          width: 16,
          height: 16,
          ml: 1,
          verticalAlign: "text-bottom",
        }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          sx={{
            position: "absolute",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : theme.palette.grey[200],
          }}
          size={16}
        />
        <CircularProgress
          variant="determinate"
          value={score}
          sx={{
            color: (theme) => getColor(score, theme),
            position: "absolute",
          }}
          size={16}
        />
      </Box>
    </Tooltip>
  );
});

const getTagColor = (tag) => {
  switch (tag) {
    case "Email Found":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#1b5e20" : "#4caf50",
          color: "#fff",
        },
      };
    case "Remote Job":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#1565c0" : "#1976d2",
          color: "#fff",
        },
      };
    case "Remote/Hybrid":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#00695c" : "#00897b",
          color: "#fff",
        },
      };
    case "Non Remote Job":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#e65100" : "#ff9800",
          color: "#fff",
        },
      };
    case "Verification Required":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#b71c1c" : "#f44336",
          color: "#fff",
        },
      };
    case "Login Required":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#b71c1c" : "#f44336",
          color: "#fff",
        },
      };
    case "Other Relevant":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#01579b" : "#03a9f4",
          color: "#fff",
        },
      };
    case "Irrelevant":
      return {
        sx: {
          backgroundColor: "transparent",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#f44336" : "#d32f2f",
          color: (theme) =>
            theme.palette.mode === "dark" ? "#f44336" : "#d32f2f",
        },
      };
    case "Uncategorized":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#424242" : "#9e9e9e",
          color: "#fff",
        },
      };
    default:
      return {
        sx: {
          backgroundColor: "transparent",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#757575" : "#9e9e9e",
          color: (theme) =>
            theme.palette.mode === "dark" ? "#757575" : "#9e9e9e",
        },
      };
  }
};

const getLocationChipColor = (location) => {
  const normalizedLocation = (location || "Unknown").toLowerCase();

  if (normalizedLocation.includes("usa") || normalizedLocation.includes("united states")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#e65100" : "#fb8c00",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("canada")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#1b5e20" : "#43a047",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("europe")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#4a148c" : "#8e24aa",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("asia")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#004d40" : "#00897b",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("latam") || normalizedLocation.includes("latin")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#424242" : "#9e9e9e",
        color: "#fff",
      },
    };
  }

  if (
    normalizedLocation.includes("australia") ||
    normalizedLocation.includes("pacific")
  ) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#6a1b9a" : "#9c27b0",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("africa")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#0d47a1" : "#1976d2",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("anonymous")) {
    return {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#424242" : "#757575",
        color: "#fff",
      },
    };
  }

  if (normalizedLocation.includes("unknown")) {
    return {
      sx: {
        backgroundColor: "transparent",
        borderColor: (theme) =>
          theme.palette.mode === "dark" ? "#9e9e9e" : "#757575",
        color: (theme) =>
          theme.palette.mode === "dark" ? "#bdbdbd" : "#616161",
      },
    };
  }

  return {
    sx: {
      backgroundColor: (theme) =>
        theme.palette.mode === "dark" ? "#37474f" : "#546e7a",
      color: "#fff",
    },
  };
};

const BidLinks = () => {
  const { user } = useAuth();
  const canManageTeamBlacklist =
    user?.role === "lead" || user?.role === "boss";
  const [bidLinks, setBidLinks] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("user123");
  const [users, setUsers] = useState({});
  const [showFilter, setShowFilter] = useState(() => {
    const stored = localStorage.getItem("showFilter");
    return stored ? JSON.parse(stored) : "all";
  });
  const [openBlacklistDialog, setOpenBlacklistDialog] = useState(false);
  const [blacklists, setBlacklists] = useState([]);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isReloadingBids, setIsReloadingBids] = useState(false);
  const [isSearchInputLoading, setIsSearchInputLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [teamCategories, setTeamCategories] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = localStorage.getItem("rowsPerPage");
    return stored ? parseInt(stored, 10) : 50;
  });
  const [teamMembers, setTeamMembers] = useState({});
  const [teamInfo, setTeamInfo] = useState(null); // Add team info state
  const [showHidden, setShowHidden] = useState(false);
  const [queryDateLimit, setQueryDateLimit] = useState(() => {
    const stored = localStorage.getItem("queryDateLimit");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [-1];
      }
    }
    return [-1];
  });
  const [viewMode, setViewMode] = useState("categories");
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [confidenceFilter, setConfidenceFilter] = useState(-1); // -1 means no filter
  const [sortBy, setSortBy] = useState("confidence"); // 'date' or 'confidence'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [anchorEl, setAnchorEl] = useState(null);
  const [confidenceRange, setConfidenceRange] = useState([0.6, 1]); // Range from 0 to 1
  const [hiddenCategories, setHiddenCategories] = useState(() => {
    const stored = localStorage.getItem("hiddenCategories");
    return stored ? JSON.parse(stored) : [];
  });
  const lastFetchTime = useRef(null);
  const [openedLinks, setOpenedLinks] = useState(() => getOpenedLinks());
  const [searchResultsPage, setSearchResultsPage] = useState(0);
  const [searchResultsRowsPerPage, setSearchResultsRowsPerPage] = useState(10);
  const [strictlyFilteredJobs, setStrictlyFilteredJobs] = useState(() => {
    const stored = localStorage.getItem("strictlyFilteredJobs");
    return stored ? JSON.parse(stored) : false;
  });
  // Add new state for tag visibility
  const [visibleTags, setVisibleTags] = useState(() => {
    const stored = localStorage.getItem("visibleTags");
    return stored
      ? normalizeVisibleTags(JSON.parse(stored))
      : createDefaultVisibleTags();
  });

  useEffect(() => {
    const stored = localStorage.getItem("visibleTags");
    const normalized = stored
      ? normalizeVisibleTags(JSON.parse(stored))
      : createDefaultVisibleTags();
    const storedKeys = stored ? Object.keys(JSON.parse(stored)) : [];
    const missingTags = STRICT_TAGS.some((tag) => !storedKeys.includes(tag));
    if (missingTags || JSON.stringify(normalized) !== stored) {
      localStorage.setItem("visibleTags", JSON.stringify(normalized));
      setVisibleTags(normalized);
    }
  }, []);

  // Add new state for sidebar sections
  const [expandedSections, setExpandedSections] = useState({
    dateTimeFilter: true, // Replace dateFilter and timeRange with dateTimeFilter
    categoryFilter: true,
    search: true,
    actions: true,
    confidence: true,
    sorting: true,
    strictFiltering: true,
    location: true,
    techStack: true,
    jobsite: true,
  });

  // Add location filter state
  const [locationFilter, setLocationFilter] = useState(() => {
    const stored = localStorage.getItem("locationFilter");
    return stored ? JSON.parse(stored) : [];
  });

  // Add location options — standard team regions plus any locations found in links
  const locationOptions = useMemo(() => {
    const uniqueFromLinks = [
      ...new Set(bidLinks.map(getLinkLocation).filter(Boolean)),
    ];

    const standardInList = STANDARD_LOCATIONS.filter((loc) =>
      uniqueFromLinks.includes(loc)
    );
    const extraLocations = uniqueFromLinks
      .filter((loc) => !STANDARD_LOCATIONS.includes(loc))
      .sort((a, b) => a.localeCompare(b));

    return [
      { value: "all", label: "Anywhere", count: 0 },
      ...[...standardInList, ...extraLocations].map((loc) => ({
        value: loc,
        label: loc,
        count: 0,
      })),
    ];
  }, [bidLinks]);

  // Tech stack filter state
  const [techStackFilter, setTechStackFilter] = useState(() => {
    const stored = localStorage.getItem("techStackFilter");
    return stored ? JSON.parse(stored) : [];
  });

  // Jobsite filter state
  const [jobsiteFilter, setJobsiteFilter] = useState(() => {
    const stored = localStorage.getItem("jobsiteFilter");
    return stored ? JSON.parse(stored) : ["all"];
  });

  const jobsiteKeywords = useMemo(
    () => normalizeJobsiteKeywords(teamInfo?.jobsiteKeywords),
    [teamInfo?.jobsiteKeywords]
  );

  const checkIsJobsiteLink = useCallback(
    (link) => isJobsiteLink(link, jobsiteKeywords),
    [jobsiteKeywords]
  );

  const jobsiteOptions = [
    { value: "all", label: "All" },
    { value: "jobsite", label: "Jobsite" },
    { value: "non-jobsite", label: "Non-jobsite" },
  ];

  // Tech stack options: from team settings only
  const techStackOptions = useMemo(() => {
    const stacks = teamInfo?.techStacks;
    if (stacks && Array.isArray(stacks) && stacks.length > 0) {
      return [
        { value: "all", label: "All", count: 0 },
        ...stacks.map((s) => ({
          value: s.label,
          label: s.label,
          description: s.description || "",
          count: 0,
        })),
        { value: "N/A", label: "N/A", count: 0 },
      ];
    }
    return [{ value: "all", label: "All", count: 0 }];
  }, [teamInfo?.techStacks]);

  // Add new state for sidebar visibility
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const stored = localStorage.getItem("sidebarVisible");
    return stored ? JSON.parse(stored) : false; // Change default to false
  });

  const [showMoreFilters, setShowMoreFilters] = useState(() => {
    const stored = localStorage.getItem("showMoreFilters");
    return stored ? JSON.parse(stored) : false;
  });


  // Update the state to use single date instead of date range
  const [dateTimeFilter, setDateTimeFilter] = useState(() => {
    const stored = localStorage.getItem("dateTimeFilter");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          selectedDate: TimezoneUtils.getTodayForInput(),
          timeRange: [-1], // Default to "All"
        };
      }
    }
    return {
      selectedDate: TimezoneUtils.getTodayForInput(),
      timeRange: [-1], // Default to "All"
    };
  });

  const getRelativeTimeString = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle days + hours specially
    if (diffInSeconds >= 86400) {
      // More than a day
      const days = Math.floor(diffInSeconds / 86400);
      const remainingHours = Math.floor((diffInSeconds % 86400) / 3600);

      if (remainingHours === 0) {
        return days === 1 ? "1 day ago" : `${days} days ago`;
      }
      return days === 1
        ? `1 day ${remainingHours} ${
            remainingHours === 1 ? "hour" : "hours"
          } ago`
        : `${days} days ${remainingHours} ${
            remainingHours === 1 ? "hour" : "hours"
          } ago`;
    }
    const intervals = {
      hour: 3600,
      minute: 60,
    };

    for (const [unit, seconds] of Object.entries(intervals)) {
      const interval = Math.floor(diffInSeconds / seconds);
      if (interval >= 1) {
        return interval === 1 ? `1 ${unit} ago` : `${interval} ${unit}s ago`;
      }
    }

    return "just now";
  };

  const getQueriesForCategory = (category) => {
    const timeLimits = Array.isArray(dateTimeFilter.timeRange)
      ? dateTimeFilter.timeRange
      : [dateTimeFilter.timeRange];
    const queries = bidLinks
      .filter((link) => {
        // First apply time range filter
        if (!matchesPostedDateTimeRanges(link, timeLimits)) return false;

        // Then apply category filter
        return (
          category === "all" ||
          getLinkDisplayCategory(link, teamCategories) === category
        );
      })
      .reduce((acc, link) => {
        if (link.queryId?.link) {
          acc.add(link.queryId.link);
        }
        return acc;
      }, new Set());
    return Array.from(queries);
  };

  // 1. Create a centralized filtering function
  const applyBehaviorFilter = (link) => {
    if (!strictlyFilteredJobs) {
      return true; // No filtering when strictlyFilteredJobs is false
    }

    const tag = link.final_details?.tag;
    
    // If "All" is selected, show everything
    if (visibleTags["All"]) {
      return true;
    }
    
    // If "Uncategorized" is selected, only show links without valid tags
    if (visibleTags["Uncategorized"]) {
      return !tag || !STRICT_TAGS.includes(tag);
    }
    
    // Filter by specific tags - must have a valid tag and it must be visible
    return tag && STRICT_TAGS.includes(tag) && visibleTags[tag];
  };

  // 2. Simplify the handleTagFilterChange function
  const handleTagFilterChange = (tag) => {
    let newVisibleTags;

    if (tag === "All") {
      // Select only "All" and turn OFF strictlyFilteredJobs
      newVisibleTags = STRICT_TAGS.reduce((acc, t) => {
        acc[t] = t === "All";
        return acc;
      }, {});
      
      // Turn OFF strictlyFilteredJobs when "All" is selected
      setStrictlyFilteredJobs(false);
      localStorage.setItem("strictlyFilteredJobs", JSON.stringify(false));
    } else if (tag === "Uncategorized") {
      // Select only "Uncategorized" and turn ON strictlyFilteredJobs
      newVisibleTags = STRICT_TAGS.reduce((acc, t) => {
        acc[t] = t === "Uncategorized";
        return acc;
      }, {});
      
      // Turn ON strictlyFilteredJobs when "Uncategorized" is selected
      setStrictlyFilteredJobs(true);
      localStorage.setItem("strictlyFilteredJobs", JSON.stringify(true));
    } else {
      // For specific tags, deselect "All" and toggle the specific tag
      const isCurrentlySelected = visibleTags[tag];
      
      if (isCurrentlySelected) {
        // Deselect this tag, if no tags selected, select "All"
        newVisibleTags = {
          ...visibleTags,
          All: false,
          Uncategorized: false, // Explicitly deselect Uncategorized
          [tag]: false,
        };
        
        // If no tags are selected, default to "All" and turn OFF strictlyFilteredJobs
        if (!Object.values(newVisibleTags).some(v => v)) {
          newVisibleTags["All"] = true;
          setStrictlyFilteredJobs(false);
          localStorage.setItem("strictlyFilteredJobs", JSON.stringify(false));
        } else {
          // Keep strictlyFilteredJobs ON when specific tags are selected
          setStrictlyFilteredJobs(true);
          localStorage.setItem("strictlyFilteredJobs", JSON.stringify(true));
        }
      } else {
        // Select this tag and deselect "All" and "Uncategorized"
        newVisibleTags = {
          ...visibleTags,
          All: false,
          Uncategorized: false, // Explicitly deselect Uncategorized
          [tag]: true,
        };
        
        // Turn ON strictlyFilteredJobs when specific tags are selected
        setStrictlyFilteredJobs(true);
        localStorage.setItem("strictlyFilteredJobs", JSON.stringify(true));
      }
    }

    setVisibleTags(newVisibleTags);
    localStorage.setItem("visibleTags", JSON.stringify(newVisibleTags));
    setPage(0);
  };

  // 3. Update the filterLink function to use centralized logic
  const filterLink = (link) => {
    // Add hidden category filter
    if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
      return false;
    }

    if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
      return false;
    }

    // Category filter
    if (
      selectedCategory !== "all" &&
      getLinkDisplayCategory(link, teamCategories) !== selectedCategory
    ) {
      return false;
    }

    // Query filter - only apply if specific queries are selected
    if (selectedQueries.length > 0) {
      if (!selectedQueries.includes(link.queryId?.link)) return false;
    }

    // Confidence range filter
    const confidence = link.confidence || 0;
    if (confidence < confidenceRange[0] || confidence > confidenceRange[1]) {
      return false;
    }

    // Location filter
    if (locationFilter.length > 0 && !locationFilter.includes("all")) {
      const linkLocation = getLinkLocation(link);

      const matchesLocation = locationFilter.some((filter) => {
        return linkLocation === filter;
      });

      if (!matchesLocation) return false;
    }

    // Tech stack filter
    if (techStackFilter.length > 0 && !techStackFilter.includes("all")) {
      const linkTechStack = getLinkTechStack(link);
      const matchesTechStack = techStackFilter.some((filter) => linkTechStack === filter);
      if (!matchesTechStack) return false;
    }

    // Jobsite filter
    if (jobsiteFilter.length > 0 && !jobsiteFilter.includes("all")) {
      const linkIsJobsite = checkIsJobsiteLink(link);
      const matchesJobsite =
        (jobsiteFilter.includes("jobsite") && linkIsJobsite) ||
        (jobsiteFilter.includes("non-jobsite") && !linkIsJobsite);
      if (!matchesJobsite) return false;
    }

    // User filter criteria
    if (showFilter === "mine" && link.created_by !== currentUserId) {
      return false;
    }

    // Apply behavior filtering using centralized function
    if (!applyBehaviorFilter(link)) {
      return false;
    }

    return true;
  };

  const countLinksForTechStackOption = (optionValue) =>
    bidLinks.filter((link) => {
      if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
        return false;
      }

      if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) return false;

      if (
        selectedCategory !== "all" &&
        getLinkDisplayCategory(link, teamCategories) !== selectedCategory
      ) {
        return false;
      }

      const confidence = link.confidence || 0;
      if (confidence < confidenceRange[0] || confidence > confidenceRange[1]) {
        return false;
      }

      const linkLocation = getLinkLocation(link);
      if (
        locationFilter.length > 0 &&
        !locationFilter.includes("all") &&
        !locationFilter.includes(linkLocation)
      ) {
        return false;
      }

      if (optionValue !== "all") {
        const linkTechStack = getLinkTechStack(link);
        if (linkTechStack !== optionValue) return false;
      }

      if (showFilter === "mine" && link.created_by !== currentUserId) {
        return false;
      }

      return applyBehaviorFilter(link);
    }).length;

  const filteredBidLinks = useMemo(() => {
    // Filter the links first
    const filtered = bidLinks.filter(filterLink);

    // Then apply sorting — jobsite links (team-configured keywords) first
    const sorted = filtered.sort((a, b) => {
      const jobsiteA = checkIsJobsiteLink(a);
      const jobsiteB = checkIsJobsiteLink(b);
      if (jobsiteA !== jobsiteB) {
        return jobsiteA ? -1 : 1;
      }

      let comparison = 0;

      if (sortBy === "confidence") {
        const confidenceA = a.confidence || 0;
        const confidenceB = b.confidence || 0;
        comparison = confidenceA - confidenceB;
      } else if (sortBy === "date") {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        comparison = dateA - dateB;
      }

      // If primary sort values are equal, apply secondary sorting by behavior (tag priority)
      if (comparison === 0) {
        const tagA = a.final_details?.tag;
        const tagB = b.final_details?.tag;
        
        // Get priority values, defaulting to lowest priority for uncategorized items
        const priorityA = TAG_PRIORITY[tagA] || TAG_PRIORITY["Uncategorized"];
        const priorityB = TAG_PRIORITY[tagB] || TAG_PRIORITY["Uncategorized"];
        
        // Lower priority number = higher priority (1 is highest, 9 is lowest)
        comparison = priorityB - priorityA;
      }

      // Apply sort order
      return sortOrder === "desc" ? -comparison : comparison;
    });

    return sorted;
  }, [
    bidLinks,
    selectedCategory,
    selectedQueries,
    confidenceRange,
    locationFilter,
    techStackFilter,
    jobsiteFilter,
    checkIsJobsiteLink,
    showFilter,
    currentUserId,
    strictlyFilteredJobs,
    visibleTags,
    hiddenCategories,
    teamCategories,
    dateTimeFilter.timeRange,
    sortBy, // Add sorting dependencies
    sortOrder, // Add sorting dependencies
  ]);

  // Calculate location counts
  const locationCounts = useMemo(() => {
    const counts = {};
    locationOptions.forEach((option) => {
      if (option.value === "all") {
        counts[option.value] = bidLinks.length;
      } else {
        counts[option.value] = bidLinks.filter(
          (link) => getLinkLocation(link) === option.value
        ).length;
      }
    });
    return counts;
  }, [bidLinks, locationOptions]);

  // Calculate tech stack counts
  const techStackCounts = useMemo(() => {
    const counts = {};
    techStackOptions.forEach((option) => {
      if (option.value === "all") {
        counts[option.value] = bidLinks.length;
      } else {
        counts[option.value] = bidLinks.filter(
          (link) => getLinkTechStack(link) === option.value
        ).length;
      }
    });
    return counts;
  }, [bidLinks]);

  // Add these useEffects to reset page when category, viewMode, or dateTimeFilter changes
  useEffect(() => {
    setPage(0);
  }, [
    selectedCategory,
    viewMode,
    dateTimeFilter.timeRange,
    dateTimeFilter.selectedDate,
  ]);

  const handleHideBidLink = async (linkId, hidden = true) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/bid-links/${linkId}/hidden`,
        { hidden }
      );
      await fetchBidLinks();
      toast.success(hidden ? "Link hidden" : "Link unhidden");
    } catch (error) {
      console.error("Failed to update hidden state:", error);
      toast.error("Failed to update link visibility");
    }
  };

  // Modified fetchBidLinks function
  const fetchBidLinks = async () => {
    try {
      setIsReloadingBids(true);

      // Convert selectedDate to UTC timezone's 00:00:00 to 23:59:59
      const fromDate = new Date(dateTimeFilter.selectedDate);
      const fromGMT = TimezoneUtils.startOfDayUTC(fromDate);
      const toGMT = TimezoneUtils.endOfDayUTC(fromDate);

      const params = new URLSearchParams({
        showBlacklisted: "false",
        showHidden: showHidden ? "true" : "false",
        from: fromGMT.toISOString(),
        to: toGMT.toISOString(),
        all: "true",
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/bid-links?${params.toString()}`
      );

      const sortedLinks = (response.data.bidLinks || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      lastFetchTime.current = new Date();

      const sanitizedLinks = sortedLinks.map((link) => ({
        ...link,
        url: stripTrackingParams(link.url),
        final_details: link.final_details
          ? {
              ...link.final_details,
              finalUrl: stripTrackingParams(link.final_details.finalUrl),
            }
          : link.final_details,
      }));

      setBidLinks(sanitizedLinks);

      // Show sidebar when links are loaded for the first time
      if (!lastFetchTime.current || bidLinks.length === 0) {
        setSidebarVisible(true);
        localStorage.setItem("sidebarVisible", JSON.stringify(true));
      }
    } catch (err) {
      console.error("Failed to fetch bid links:", err);
    } finally {
      setIsReloadingBids(false);
    }
  };

  // Set up auto-refresh every 30 minutes
  useEffect(() => {
    fetchBidLinks(); // Initial fetch

    const intervalId = setInterval(() => {
      fetchBidLinks();
    }, 30 * 60 * 1000); // 30 minutes in milliseconds

    return () => clearInterval(intervalId);
  }, [dateTimeFilter.selectedDate, showHidden]); // Re-run when selected date or hidden toggle changes

  useEffect(() => {
    const fetchTeamCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/categories`
        );
        setTeamCategories(response.data.categories || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setTeamCategories([]);
      }
    };

    fetchTeamCategories();
  }, []);

  useEffect(() => {
    const uniqueCategories = [
      ...new Set(
        bidLinks.map((link) => getLinkDisplayCategory(link, teamCategories))
      ),
    ];
    const sorted = uniqueCategories
      .filter((category) => category !== OTHER_CATEGORY)
      .sort();
    if (uniqueCategories.includes(OTHER_CATEGORY)) {
      sorted.push(OTHER_CATEGORY);
    }

    setCategories(sorted);
  }, [bidLinks, teamCategories]);

  const fetchBlacklists = async (isTeam = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/bid-links/blacklist`,
        {
          params: { isTeam: isTeam },
        }
      );
      setBlacklists(response.data.blacklists || []);
    } catch (error) {
      console.error("Failed to fetch blacklists:", error);
      toast.error("Failed to fetch blacklists");
    }
  };

  const handleAddBlacklist = async (newBlackWords, isTeam = false) => {
    if (newBlackWords == null || newBlackWords === "") return;
    try {
      // Split by comma only; preserve spaces inside each segment
      const blacklistArray = newBlackWords
        .split(",")
        .filter((word) => word.length > 0);

      if (blacklistArray.length === 0) return;

      await axios.post(`${process.env.REACT_APP_API_URL}/bid-links/blacklist`, {
        blacklists: blacklistArray,
        isTeam: isTeam,
      });
      await fetchBlacklists(isTeam);
      await fetchBidLinks();
      toast.success(
        `${blacklistArray.length} item(s) added to ${
          isTeam ? "team" : "personal"
        } blacklist`
      );
    } catch (error) {
      console.error("Failed to add to blacklist:", error);
      toast.error("Failed to add to blacklist");
    }
  };

  const handleAddCompanyToBlacklist = async (company) => {
    if (!company) return;

    // Optimistically update UI by filtering out links with this company
    const originalLinks = [...bidLinks];
    setBidLinks((prev) => prev.filter((link) => link.company !== company));

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/bid-links/blacklist`, {
        blacklists: [`company:${company}`],
        isTeam: true, // Always add to team blacklist
      });
      await fetchBlacklists(true); // Fetch team blacklists to update the count
      toast.success(`Added ${company} to team blacklist`);
    } catch (error) {
      console.error("Failed to add company to blacklist:", error);
      toast.error("Failed to add company to blacklist");
      // Revert optimistic update on error
      setBidLinks(originalLinks);
    }
  };

  const handleDeleteBlacklist = async (urlToDelete, isTeam = false) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${isTeam ? 'team' : 'personal'} blacklist URL?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/bid-links/blacklist`,
        {
          data: {
            blacklists: [urlToDelete],
            isTeam: isTeam,
          },
        }
      );
      await fetchBlacklists(isTeam);
      await fetchBidLinks();
      toast.success(
        `URL removed from ${isTeam ? "team" : "personal"} blacklist`
      );
    } catch (error) {
      console.error("Failed to remove from blacklist:", error);
      toast.error("Failed to remove from blacklist");
    }
  };

  const handleGlobalSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setIsSearchInputLoading(true);
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        showBlacklisted: false,
      });

      params.append("confidenceMin", confidenceRange[0].toString());
      params.append("confidenceMax", confidenceRange[1].toString());

      // Check if all visible tags are true
      const allTagsVisible = Object.values(visibleTags).every(value => value === true);
      
      // Add strict filtering parameters - turn off if all tags are visible
      if (strictlyFilteredJobs && !allTagsVisible) {
        params.append("strictlyFilteredJobs", "true");
        params.append("visibleTags", JSON.stringify(visibleTags));
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/bid-links/search?${params.toString()}`
      );
      setGlobalSearchResults(Array.isArray(response.data) ? response.data : []);
      setSearchResultsPage(0);
      setOpenSearchDialog(true);
    } catch (error) {
      console.error("Failed to perform global search:", error);
      toast.error("Failed to perform global search");
    } finally {
      setIsSearchInputLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/teams/my-team`
      );
      const membersMap = {};
      response.data.forEach((member) => {
        membersMap[member._id] = member.name;
      });
      setTeamMembers(membersMap);
    } catch (error) {
      console.error("Failed to fetch team members:", error);
      toast.error("Failed to fetch team members");
    }
  };

  // Add new function to fetch team information
  const fetchTeamInfo = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/teams/my-team-info`
      );
      setTeamInfo(response.data.team);
    } catch (error) {
      console.error("Failed to fetch team info:", error);
      // Don't show error toast as this is not critical
    }
  };

  useEffect(() => {
    fetchTeamMembers();
    fetchTeamInfo(); // Add this call
  }, []);

  // Add this useEffect to scroll to top when page changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [page]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderBlacklistPanel = () => {
    const [isAddingBlacklist, setIsAddingBlacklist] = useState(false);
    const [blacklistType, setBlacklistType] = useState(
      canManageTeamBlacklist ? "team" : "personal"
    );

    // Add this useEffect to fetch the blacklists when the panel opens
    useEffect(() => {
      if (openBlacklistDialog) {
        fetchBlacklists(canManageTeamBlacklist && blacklistType === "team");
      }
    }, [openBlacklistDialog]);

    const handleBlacklistSubmit = async (value) => {
      if (!value.trim()) return;
      setIsAddingBlacklist(true);
      try {
        await handleAddBlacklist(
          value,
          canManageTeamBlacklist && blacklistType === "team"
        );
        // Clear the input value after successful addition
        return true; // Return true to indicate success
      } finally {
        setIsAddingBlacklist(false);
      }
    };

    return (
      <Drawer
        anchor="right"
        open={openBlacklistDialog}
        onClose={() => setOpenBlacklistDialog(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "400px",
            padding: 2,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Manage Blacklisted Words
          </Typography>

          {canManageTeamBlacklist ? (
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Box sx={{ display: "flex" }}>
                <Button
                  variant={blacklistType === "personal" ? "contained" : "text"}
                  onClick={() => {
                    setBlacklistType("personal");
                    fetchBlacklists(false);
                  }}
                  sx={{ flex: 1, borderRadius: "4px 0 0 4px" }}
                >
                  My Blacklist
                </Button>
                <Button
                  variant={blacklistType === "team" ? "contained" : "text"}
                  onClick={() => {
                    setBlacklistType("team");
                    fetchBlacklists(true);
                  }}
                  sx={{ flex: 1, borderRadius: "0 4px 4px 0" }}
                >
                  Team Blacklist
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              My Blacklist
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Enter blacklists"
              variant="standard"
              size="small"
              helperText="Separate multiple items with commas"
              disabled={isAddingBlacklist}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  const success = await handleBlacklistSubmit(e.target.value);
                  if (success) {
                    e.target.value = "";
                  }
                }
              }}
              InputProps={{
                endAdornment: isAddingBlacklist && (
                  <CircularProgress size={20} />
                ),
              }}
            />
          </Box>

          <List sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            {[...blacklists].reverse().map((url, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() =>
                      handleDeleteBlacklist(
                        url,
                        canManageTeamBlacklist && blacklistType === "team"
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={url}
                  sx={{
                    wordBreak: "break-all",
                    pr: 2,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    );
  };

  const blacklistButton = (
    <Button
      variant="outlined"
      startIcon={<DoNotTouchIcon />}
      onClick={() => setOpenBlacklistDialog(true)}
      size="small"
    >
      Blacklists
    </Button>
  );

  // Helper function to format queryDateLimit for LinkDetailsDialog
  const formatQueryDateLimit = (days) => {
    if (days === null || days === undefined) return "Any time";
    switch (days) {
      case 1:
        return "Past 24 hours";
      case 7:
        return "Past week";
      case 30:
        return "Past month";
      case 365:
        return "Past year";
      default:
        return `${days} days`;
    }
  };

  // Helper function to get user name for LinkDetailsDialog
  const getUserNameById = (userId) => {
    return teamMembers[userId] || users[userId]?.name || "Unknown";
  };

  const FilteredBidLinks = React.memo(({ filteredBidLinks, users }) => {
    const [goToPage, setGoToPage] = useState("");
    const emptyRows =
      page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - filteredBidLinks.length)
        : 0;

    if (filteredBidLinks.length === 0) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No links
          </Typography>
        </Box>
      );
    }

    const PaginationComponent = () => {
      const totalPages = Math.ceil(filteredBidLinks.length / rowsPerPage);

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
            gap: 2,
          }}
        >
          <TablePagination
            component="div"
            count={filteredBidLinks.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const newRowsPerPage = parseInt(event.target.value, 10);
              setRowsPerPage(newRowsPerPage);
              localStorage.setItem("rowsPerPage", newRowsPerPage.toString());
              setPage(0);
            }}
            showFirstButton
            showLastButton
          />
        </Box>
      );
    };

    return (
      <Box sx={{ width: "100%" }}>
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <PaginationComponent />
          <TableContainer sx={{ minWidth: "100%" }}>
            <Table aria-labelledby="tableTitle">
              <TableBody>
                {filteredBidLinks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((link, index) => {
                    const fullIndex = page * rowsPerPage + index + 1;
                    const displayUrl =
                      link.final_details?.finalUrl || link.url;
                    const sanitizedDisplayUrl = stripTrackingParams(displayUrl);
                    return (
                      <TableRow
                        hover
                        key={link._id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.02) !important",
                          },
                          "&:nth-of-type(even)": {
                            backgroundColor: "rgba(0, 0, 0, 0.01)",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1.5, px: 3 }}>
                          {fullIndex}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, px: 3 }}>
                          <Link
                            href={sanitizedDisplayUrl}
                            target="_blank"
                            rel={EXTERNAL_LINK_REL}
                            sx={{
                              fontSize: "1.1rem",
                              fontFamily:
                                '"Inter","Roboto","Helvetica","Arial",sans-serif',
                              fontWeight: 500,
                              textDecoration: "none",
                              color: "primary.main",
                              display: "inline-flex",
                              alignItems: "center",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                              "&:visited": {
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#e0b0ff" // Light purple for dark mode
                                    : "#551A8B", // Standard visited purple for light mode
                              },
                              ...(openedLinks.some(
                                (openedLink) =>
                                  openedLink.url === sanitizedDisplayUrl
                              ) && {
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#e0b0ff" // Light purple for dark mode
                                    : "#551A8B", // Standard visited purple for light mode
                              }),
                            }}
                          >
                            {link.title}
                            {link.confidence && (
                              <ConfidenceIndicator
                                confidence={link.confidence}
                              />
                            )}
                          </Link>
                          <Chip
                            label={getLinkLocation(link)}
                            size="small"
                            sx={{
                              ml: 1,
                              ...getLocationChipColor(getLinkLocation(link)).sx,
                            }}
                          />
                          {link.final_details?.tag && (
                            <Chip
                              label={link.final_details.tag}
                              size="small"
                              sx={{
                                ml: 1,
                                ...getTagColor(link.final_details.tag).sx,
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 1,
                              lineHeight: 1.6,
                            }}
                          >
                            {(link.company || link.date) && (
                              <>
                                <Box
                                  component="span"
                                  sx={getPostedDateTextSx(link)}
                                >
                                  {formatLinkPostedDate(link.date, getRelativeTimeString) || "N/A"}
                                </Box>
                                {link.company && (
                                  <Box component="span">{" - "}{link.company}</Box>
                                )}
                                <br />
                              </>
                            )}
                            {link.description}
                          </Typography>
                          {renderLinkTechStacks(link)}
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Tooltip title="Show Details" placement="left">
                              <Button
                                size="small"
                                onClick={() => setSelectedLink(link)}
                                sx={{
                                  minWidth: "40px",
                                  height: "40px",
                                  borderRadius: 1,
                                }}
                              >
                                <InfoIcon color="action" />
                              </Button>
                            </Tooltip>
                            {link.company && canManageTeamBlacklist && (
                              <Tooltip
                                title={`Add ${link.company} to team blacklist`}
                                placement="left"
                              >
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleAddCompanyToBlacklist(link.company)
                                  }
                                  sx={{
                                    minWidth: "40px",
                                    height: "40px",
                                    borderRadius: 1,
                                  }}
                                >
                                  <DoNotTouchIcon color="action" />
                                </Button>
                              </Tooltip>
                            )}
                            {(user?.role === "lead" || user?.role === "boss") && (
                              <Tooltip
                                title={link.hidden ? "Unhide link" : "Hide link"}
                                placement="left"
                              >
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleHideBidLink(link._id, !link.hidden)
                                  }
                                  sx={{
                                    minWidth: "40px",
                                    height: "40px",
                                    borderRadius: 1,
                                  }}
                                >
                                  <VisibilityOffIcon color={link.hidden ? "error" : "action"} />
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationComponent />
        </Paper>
      </Box>
    );
  });

  const handleLocationFilterChange = (location) => {
    let newLocationFilter;
    if (location === "all") {
      newLocationFilter = ["all"];
    } else if (locationFilter.includes("all")) {
      newLocationFilter = [location];
    } else if (locationFilter.includes(location)) {
      newLocationFilter = locationFilter.filter((l) => l !== location);
      if (newLocationFilter.length === 0) {
        newLocationFilter = ["all"];
      }
    } else {
      newLocationFilter = [...locationFilter, location];
    }

    setLocationFilter(newLocationFilter);
    localStorage.setItem("locationFilter", JSON.stringify(newLocationFilter));
    setPage(0);
  };

  const handleTechStackFilterChange = (techStack) => {
    let newTechStackFilter;
    if (techStack === "all") {
      newTechStackFilter = ["all"];
    } else if (techStackFilter.includes("all")) {
      newTechStackFilter = [techStack];
    } else if (techStackFilter.includes(techStack)) {
      newTechStackFilter = techStackFilter.filter((t) => t !== techStack);
      if (newTechStackFilter.length === 0) {
        newTechStackFilter = ["all"];
      }
    } else {
      newTechStackFilter = [...techStackFilter, techStack];
    }

    setTechStackFilter(newTechStackFilter);
    localStorage.setItem("techStackFilter", JSON.stringify(newTechStackFilter));
    setPage(0);
  };

  const handleJobsiteFilterChange = (jobsite) => {
    let newJobsiteFilter;
    if (jobsite === "all") {
      newJobsiteFilter = ["all"];
    } else if (jobsiteFilter.includes("all")) {
      newJobsiteFilter = [jobsite];
    } else if (jobsiteFilter.includes(jobsite)) {
      newJobsiteFilter = jobsiteFilter.filter((value) => value !== jobsite);
      if (newJobsiteFilter.length === 0) {
        newJobsiteFilter = ["all"];
      }
    } else {
      newJobsiteFilter = [...jobsiteFilter, jobsite];
    }

    setJobsiteFilter(newJobsiteFilter);
    localStorage.setItem("jobsiteFilter", JSON.stringify(newJobsiteFilter));
    setPage(0);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderSidebarSection = (title, isExpanded, onToggle, children) => (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          cursor: "pointer",
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={onToggle}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "cyan" }}>
          {title}
        </Typography>
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      {isExpanded && (
        <Box
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 1,
            mt: 1,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );

  const renderSidebar = () => (
    <Card
      sx={{
        width: 320,
        height: "calc(100vh - 60px)",
        position: "sticky",
        top: 20,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%", overflow: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Reload links">
              <IconButton
                size="small"
                onClick={fetchBidLinks}
                disabled={isReloadingBids}
                sx={{ ml: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => {
                const newVisible = !sidebarVisible;
                setSidebarVisible(newVisible);
                localStorage.setItem(
                  "sidebarVisible",
                  JSON.stringify(newVisible)
                );
              }}
              sx={{ ml: 1 }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Date and Time Filter Section */}
        {renderSidebarSection(
          "Date and Time Filter",
          expandedSections.dateTimeFilter,
          () => toggleSection("dateTimeFilter"),
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Single Date Selection */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Date
              </Typography>
              <TextField
                variant="standard"
                type="date"
                value={dateTimeFilter.selectedDate}
                onChange={(e) => {
                  const newFilter = {
                    ...dateTimeFilter,
                    selectedDate: e.target.value,
                  };
                  setDateTimeFilter(newFilter);
                  localStorage.setItem(
                    "dateTimeFilter",
                    JSON.stringify(newFilter)
                  );
                }}
                size="small"
                fullWidth
              />
            </Box>

            {/* Time Range */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Posted Date Range
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Filter by when the job was posted
              </Typography>
              <TextField
                select
                SelectProps={{
                  multiple: true,
                  value: Array.isArray(dateTimeFilter.timeRange)
                    ? dateTimeFilter.timeRange
                    : [dateTimeFilter.timeRange],
                  onChange: (e) => {
                    const values = e.target.value;
                    let newValues = [...values];

                    const wasAllSelected =
                      dateTimeFilter.timeRange.includes(-1);
                    const isAllSelected = values.includes(-1);

                    if (isAllSelected && !wasAllSelected) {
                      newValues = [-1];
                    } else if (
                      values.some((v) => [0, 1, 7, 30, 365].includes(v))
                    ) {
                      newValues = newValues.filter((v) => v !== -1);
                    } else if (values.includes(-1)) {
                      newValues = [-1];
                    }

                    if (newValues.length === 0) {
                      newValues = [-1];
                    }

                    const newFilter = {
                      ...dateTimeFilter,
                      timeRange: newValues,
                    };
                    setDateTimeFilter(newFilter);
                    localStorage.setItem(
                      "dateTimeFilter",
                      JSON.stringify(newFilter)
                    );
                  },
                  renderValue: (selected) => {
                    if (!selected || selected.length === 0)
                      return "Select time ranges";
                    return selected
                      .map((value) => {
                        switch (value) {
                          case -1:
                            return "All";
                          case 0:
                            return "Any time";
                          case 1:
                            return "Past 24 hours";
                          case 7:
                            return "Past week";
                          case 30:
                            return "Past month";
                          case 365:
                            return "Past year";
                          default:
                            return `${value} days`;
                        }
                      })
                      .join(", ");
                  },
                }}
                variant="standard"
                size="small"
                fullWidth
              >
                <MenuItem value={-1}>All</MenuItem>
                <MenuItem value={0}>Any time</MenuItem>
                <MenuItem value={1}>Past 24 hours</MenuItem>
                <MenuItem value={7}>Past week</MenuItem>
                <MenuItem value={30}>Past month</MenuItem>
                <MenuItem value={365}>Past year</MenuItem>
              </TextField>
            </Box>
          </Box>
        )}

        {showMoreFilters &&
          renderSidebarSection(
            "Jobsite",
            expandedSections.jobsite,
            () => toggleSection("jobsite"),
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {jobsiteOptions.map((option) => {
                const jobsiteCount = bidLinks.filter((link) => {
                  const timeLimits = Array.isArray(dateTimeFilter.timeRange)
                    ? dateTimeFilter.timeRange
                    : [dateTimeFilter.timeRange];
                  if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) return false;

                  if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) return false;

                  if (
                    selectedCategory !== "all" &&
                    getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                  ) {
                    return false;
                  }

                  if (selectedQueries.length > 0 && !selectedQueries.includes(link.queryId?.link)) {
                    return false;
                  }

                  const confidence = link.confidence || 0;
                  if (confidence < confidenceRange[0] || confidence > confidenceRange[1]) {
                    return false;
                  }

                  if (locationFilter.length > 0 && !locationFilter.includes("all")) {
                    const linkLocation = getLinkLocation(link);
                    if (!locationFilter.includes(linkLocation)) return false;
                  }

                  if (techStackFilter.length > 0 && !techStackFilter.includes("all")) {
                    const linkTechStack = getLinkTechStack(link);
                    if (!techStackFilter.includes(linkTechStack)) return false;
                  }

                  if (showFilter === "mine" && link.created_by !== currentUserId) {
                    return false;
                  }

                  if (!applyBehaviorFilter(link)) return false;

                  if (option.value === "all") return true;
                  const linkIsJobsite = checkIsJobsiteLink(link);
                  if (option.value === "jobsite") return linkIsJobsite;
                  return !linkIsJobsite;
                }).length;

                return (
                  <Box
                    key={option.value}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      backgroundColor: jobsiteFilter.includes(option.value)
                        ? "action.selected"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      transition: "background-color 0.2s",
                    }}
                    onClick={() => handleJobsiteFilterChange(option.value)}
                  >
                    <Checkbox
                      checked={jobsiteFilter.includes(option.value)}
                      size="small"
                      sx={{ pointerEvents: "none" }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.label}
                    </Typography>
                    <Chip
                      label={jobsiteCount}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}

        {/* Location Filter Section - Only show if team has 'special' role */}
        {teamInfo?.role?.includes("special") &&
          renderSidebarSection(
            "Location",
            expandedSections.location,
            () => toggleSection("location"),
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {locationOptions
                .filter((option) => {
                  // Always show "all", hide others with count 0
                  if (option.value === "all") return true;
                  
                  // Calculate count with all filters applied (same as category count)
                  const locationCount = bidLinks.filter((link) => {
                    // Apply date filter
                    if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) return false;

                    // Apply category visibility filter
                    if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                      return false;
                    }

                    // Apply category filter
                    if (
                      selectedCategory !== "all" &&
                      getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                    ) {
                      return false;
                    }

                    // Apply confidence range filter
                    const confidence = link.confidence || 0;
                    if (
                      confidence < confidenceRange[0] ||
                      confidence > confidenceRange[1]
                    ) {
                      return false;
                    }

                    // Apply location filter
                    const linkLocation = getLinkLocation(link);
                    if (option.value !== "all" && linkLocation !== option.value) {
                      return false;
                    }

                    // Apply user filter criteria
                    if (
                      showFilter === "mine" &&
                      link.created_by !== currentUserId
                    ) {
                      return false;
                    }

                    // Apply behavior filtering using centralized function
                    return applyBehaviorFilter(link);
                  }).length;
                  
                  return locationCount > 0;
                })
                .map((option) => {
                  // Calculate count with all filters applied for display
                  const locationCount = option.value === "all" 
                    ? bidLinks.filter((link) => {
                        // Apply date filter
                        if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
                          return false;
                        }

                        // Apply category visibility filter
                        if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                          return false;
                        }

                        // Apply category filter
                        if (
                          selectedCategory !== "all" &&
                          getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                        ) {
                          return false;
                        }

                        // Apply confidence range filter
                        const confidence = link.confidence || 0;
                        if (
                          confidence < confidenceRange[0] ||
                          confidence > confidenceRange[1]
                        ) {
                          return false;
                        }

                        // Apply user filter criteria
                        if (
                          showFilter === "mine" &&
                          link.created_by !== currentUserId
                        ) {
                          return false;
                        }

                        // Apply behavior filtering using centralized function
                        return applyBehaviorFilter(link);
                      }).length
                    : bidLinks.filter((link) => {
                        // Apply date filter
                        if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
                          return false;
                        }

                        // Apply category visibility filter
                        if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                          return false;
                        }

                        // Apply category filter
                        if (
                          selectedCategory !== "all" &&
                          getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                        ) {
                          return false;
                        }

                        // Apply confidence range filter
                        const confidence = link.confidence || 0;
                        if (
                          confidence < confidenceRange[0] ||
                          confidence > confidenceRange[1]
                        ) {
                          return false;
                        }

                        // Apply location filter
                        const linkLocation = getLinkLocation(link);
                        if (linkLocation !== option.value) {
                          return false;
                        }

                        // Apply user filter criteria
                        if (
                          showFilter === "mine" &&
                          link.created_by !== currentUserId
                        ) {
                          return false;
                        }

                        // Apply behavior filtering using centralized function
                        return applyBehaviorFilter(link);
                      }).length;

                  return (
                    <Box
                    key={option.value}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      backgroundColor: locationFilter.includes(option.value)
                        ? "action.selected"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      transition: "background-color 0.2s",
                    }}
                    onClick={() => handleLocationFilterChange(option.value)}
                  >
                    <Checkbox
                      checked={locationFilter.includes(option.value)}
                      size="small"
                      sx={{ pointerEvents: "none" }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.label}
                    </Typography>
                    <Chip
                      label={locationCount}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
                    />
                  </Box>
                  );
                })}
            </Box>
          )}

        {/* Tech Stack Filter Section - Only show if team has 'special' role */}
        {showMoreFilters &&
          teamInfo?.role?.includes("special") &&
          renderSidebarSection(
            "Tech Stack",
            expandedSections.techStack,
            () => toggleSection("techStack"),
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {techStackOptions
                .filter((option) => {
                  if (option.value === "all") return true;
                  return countLinksForTechStackOption(option.value) > 0;
                })
                .map((option) => {
                  const techStackCount = countLinksForTechStackOption(option.value);

                  const row = (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        backgroundColor: techStackFilter.includes(option.value)
                          ? "action.selected"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        transition: "background-color 0.2s",
                      }}
                      onClick={() => handleTechStackFilterChange(option.value)}
                    >
                      <Checkbox
                        checked={techStackFilter.includes(option.value)}
                        size="small"
                        sx={{ pointerEvents: "none" }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.label}
                      </Typography>
                      <Chip
                        label={techStackCount}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
                      />
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={option.value}
                      title={option.description || ""}
                      placement="right"
                      arrow
                      disableHoverListener={!option.description}
                    >
                      {row}
                    </Tooltip>
                  );
                })}
            </Box>
          )}

        {/* Category Filter Section */}
        {showMoreFilters && renderSidebarSection(
          "Category Filter",
          expandedSections.categoryFilter,
          () => toggleSection("categoryFilter"),
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                backgroundColor:
                  selectedCategory === "all"
                    ? "action.selected"
                    : "transparent",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                transition: "background-color 0.2s",
              }}
              onClick={() => {
                setSelectedCategory("all");
                setPage(0);
              }}
            >
              <Checkbox
                checked={selectedCategory === "all"}
                size="small"
                sx={{ pointerEvents: "none" }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                All Categories
              </Typography>
              <Chip
                label={
                  bidLinks.filter((link) => {
                    // Apply date filter
                    if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) return false;

                    // Apply category visibility filter
                    if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                      return false;
                    }

                    // Apply confidence range filter
                    const confidence = link.confidence || 0;
                    if (
                      confidence < confidenceRange[0] ||
                      confidence > confidenceRange[1]
                    ) {
                      return false;
                    }

                    // Apply location filter
                    if (
                      locationFilter.length > 0 &&
                      !locationFilter.includes("all")
                    ) {
                      const linkLocation = getLinkLocation(link);
                      const matchesLocation = locationFilter.some((filter) => {
                        return linkLocation === filter;
                      });
                      if (!matchesLocation) return false;
                    }

                    // Apply tech stack filter
                    if (
                      techStackFilter.length > 0 &&
                      !techStackFilter.includes("all")
                    ) {
                      const linkTechStack = getLinkTechStack(link);
                      const matchesTechStack = techStackFilter.some((f) => linkTechStack === f);
                      if (!matchesTechStack) return false;
                    }

                    // Apply user filter criteria
                    if (
                      showFilter === "mine" &&
                      link.created_by !== currentUserId
                    ) {
                      return false;
                    }

                    // Apply behavior filtering using centralized function
                    return applyBehaviorFilter(link);
                  }).length
                }
                size="small"
                variant="outlined"
                sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
              />
            </Box>
            {categories
              .filter((category) => !hiddenCategories.includes(category))
              .map((category) => {
                const categoryCount = bidLinks.filter((link) => {
                  // Apply date filter
                  if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
                    return false;
                  }

                  // Apply category filter
                  if (getLinkDisplayCategory(link, teamCategories) !== category) {
                    return false;
                  }

                  // Apply confidence range filter
                  const confidence = link.confidence || 0;
                  if (
                    confidence < confidenceRange[0] ||
                    confidence > confidenceRange[1]
                  ) {
                    return false;
                  }

                  // Apply location filter
                  if (
                    locationFilter.length > 0 &&
                    !locationFilter.includes("all")
                  ) {
                    const linkLocation = getLinkLocation(link);
                    const matchesLocation = locationFilter.some((filter) => {
                      return linkLocation === filter;
                    });
                    if (!matchesLocation) return false;
                  }

                  // Apply tech stack filter
                  if (
                    techStackFilter.length > 0 &&
                    !techStackFilter.includes("all")
                  ) {
                    const linkTechStack = getLinkTechStack(link);
                    const matchesTechStack = techStackFilter.some((f) => linkTechStack === f);
                    if (!matchesTechStack) return false;
                  }

                  // Apply user filter criteria
                  if (
                    showFilter === "mine" &&
                    link.created_by !== currentUserId
                  ) {
                    return false;
                  }

                  // Apply behavior filtering using centralized function
                  return applyBehaviorFilter(link);
                }).length;

                // Hide category if count is 0
                if (categoryCount === 0) return null;

                return (
                  <Box
                    key={category}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      backgroundColor:
                        selectedCategory === category
                          ? "action.selected"
                          : "transparent",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      transition: "background-color 0.2s",
                    }}
                    onClick={() => {
                      setSelectedCategory(category);
                      setPage(0);
                    }}
                  >
                    <Checkbox
                      checked={selectedCategory === category}
                      size="small"
                      sx={{ pointerEvents: "none" }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {category}
                    </Typography>
                    <Chip
                      label={categoryCount}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
                    />
                  </Box>
                );
              })}
            {hiddenCategories.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 1 }}
                >
                  Hidden Categories
                </Typography>
                {categories
                  .filter((category) => hiddenCategories.includes(category))
                  .map((category) => (
                    <ListItemButton
                      key={category}
                      sx={{ pr: 16, opacity: 0.6 }}
                    >
                      <ListItemIcon>
                        <VisibilityOffIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={category}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          handleToggleCategoryVisibility(category, event)
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  ))}
              </>
            )}
          </Box>
        )}

        {/* Confidence Range & Sorting Section */}
        {showMoreFilters && renderSidebarSection(
          "Confidence & Sorting",
          expandedSections.confidence,
          () => toggleSection("confidence"),
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Confidence Range */}
            <Box sx={{ width: "90%" }}>
              <Typography variant="subtitle2" gutterBottom>
                Confidence Range
              </Typography>
              <Slider
                sx={{ marginLeft: 2, marginRight: 2 }}
                value={confidenceRange}
                onChange={(event, newValue) => {
                  setConfidenceRange(newValue);
                  setPage(0);
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                min={0.3}
                max={1}
                step={0.1}
                marks={[
                  { value: 0.3, label: "30%" },
                  { value: 0.6, label: "60%" },
                  { value: 1, label: "100%" },
                ]}
              />
            </Box>

            {/* Sorting */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                select
                variant="standard"
                size="small"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(0);
                }}
                label="Sort by"
                fullWidth
              >
                <MenuItem value="confidence">Confidence</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </TextField>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant={sortOrder === "desc" ? "contained" : "outlined"}
                  onClick={() => {
                    setSortOrder("desc");
                    setPage(0);
                  }}
                  size="small"
                  startIcon={<ArrowDownwardIcon />}
                  fullWidth
                >
                  Desc
                </Button>
                <Button
                  variant={sortOrder === "asc" ? "contained" : "outlined"}
                  onClick={() => {
                    setSortOrder("asc");
                    setPage(0);
                  }}
                  size="small"
                  startIcon={<ArrowUpwardIcon />}
                  fullWidth
                >
                  Asc
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Behavior Filtering Section - Only show if team has 'special' role */}
        {teamInfo?.role?.includes("special") &&
          renderSidebarSection(
            "Behavior Filtering",
            expandedSections.strictFiltering,
            () => toggleSection("strictFiltering"),
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {STRICT_TAGS.filter((tag) => {
                  // Calculate count first to check if we should show this tag
                  const tagCount = bidLinks.filter((link) => {
                    // Apply date filter
                    if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) return false;

                    // Apply category visibility filter
                    if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                      return false;
                    }

                    // Apply category filter
                    if (
                      selectedCategory !== "all" &&
                      getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                    ) {
                      return false;
                    }

                    // Apply confidence range filter
                    const confidence = link.confidence || 0;
                    if (
                      confidence < confidenceRange[0] ||
                      confidence > confidenceRange[1]
                    ) {
                      return false;
                    }

                    // Apply location filter
                    if (
                      locationFilter.length > 0 &&
                      !locationFilter.includes("all")
                    ) {
                      const linkLocation = getLinkLocation(link);
                      const matchesLocation = locationFilter.some((filter) => {
                        return linkLocation === filter;
                      });
                      if (!matchesLocation) return false;
                    }

                    // Apply tech stack filter
                    if (
                      techStackFilter.length > 0 &&
                      !techStackFilter.includes("all")
                    ) {
                      const linkTechStack = getLinkTechStack(link);
                      const matchesTechStack = techStackFilter.some((f) => linkTechStack === f);
                      if (!matchesTechStack) return false;
                    }

                    // Apply user filter criteria
                    if (
                      showFilter === "mine" &&
                      link.created_by !== currentUserId
                    ) {
                      return false;
                    }

                    // Apply tag filter - special handling for "All" and "Uncategorized"
                    if (tag === "All") {
                      // For "All", count all links (including uncategorized)
                      return true;
                    } else if (tag === "Uncategorized") {
                      // For "Uncategorized", count links that have no tag or an invalid tag
                      const linkTag = link.final_details?.tag;
                      return !linkTag || !STRICT_TAGS.includes(linkTag);
                    } else {
                      // For specific tags, count only links with that exact tag
                      const linkTag = link.final_details?.tag;
                      return linkTag === tag;
                    }
                  }).length;

                  // Always show "All", hide others with count 0
                  if (tag === "All") return true;
                  return tagCount > 0;
                }).map((tag) => {
                  const tagCount = bidLinks.filter((link) => {
                    // Apply date filter
                    if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) return false;

                    // Apply category visibility filter
                    if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                      return false;
                    }

                    // Apply category filter
                    if (
                      selectedCategory !== "all" &&
                      getLinkDisplayCategory(link, teamCategories) !== selectedCategory
                    ) {
                      return false;
                    }

                    // Apply confidence range filter
                    const confidence = link.confidence || 0;
                    if (
                      confidence < confidenceRange[0] ||
                      confidence > confidenceRange[1]
                    ) {
                      return false;
                    }

                    // Apply location filter
                    if (
                      locationFilter.length > 0 &&
                      !locationFilter.includes("all")
                    ) {
                      const linkLocation = getLinkLocation(link);
                      const matchesLocation = locationFilter.some((filter) => {
                        return linkLocation === filter;
                      });
                      if (!matchesLocation) return false;
                    }

                    // Apply tech stack filter
                    if (
                      techStackFilter.length > 0 &&
                      !techStackFilter.includes("all")
                    ) {
                      const linkTechStack = getLinkTechStack(link);
                      const matchesTechStack = techStackFilter.some((f) => linkTechStack === f);
                      if (!matchesTechStack) return false;
                    }

                    // Apply user filter criteria
                    if (
                      showFilter === "mine" &&
                      link.created_by !== currentUserId
                    ) {
                      return false;
                    }

                    // Apply tag filter - special handling for "All" and "Uncategorized"
                    if (tag === "All") {
                      // For "All", count all links (including uncategorized)
                      return true;
                    } else if (tag === "Uncategorized") {
                      // For "Uncategorized", count links that have no tag or an invalid tag
                      const linkTag = link.final_details?.tag;
                      return !linkTag || !STRICT_TAGS.includes(linkTag);
                    } else {
                      // For specific tags, count only links with that exact tag
                      const linkTag = link.final_details?.tag;
                      return linkTag === tag;
                    }
                  }).length;

                  return (
                    <Box
                      key={tag}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        backgroundColor: visibleTags[tag]
                          ? "action.selected"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        transition: "background-color 0.2s",
                      }}
                      onClick={() => handleTagFilterChange(tag)}
                    >
                      <Checkbox
                        checked={visibleTags[tag]}
                        size="small"
                        sx={{ pointerEvents: "none" }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {getBehaviorFilterLabel(tag)}
                      </Typography>
                      {tag === "Email Found" && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            extractEmails();
                          }}
                        >
                          Extract
                        </Button>
                      )}
                      <Chip
                        label={tagCount}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 40, height: 20, fontSize: "0.75rem" }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

        <Box
          sx={{
            mt: 2,
            pt: 1,
            pb: 0.5,
            backgroundColor: "transparent",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              const nextState = !showMoreFilters;
              setShowMoreFilters(nextState);
              localStorage.setItem("showMoreFilters", JSON.stringify(nextState));
            }}
            fullWidth
            size="medium"
            startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            {showMoreFilters ? "Hide more filters" : "More filters"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSidebarToggle = () => (
    <IconButton
      size="small"
      onClick={() => {
        const newVisible = !sidebarVisible;
        setSidebarVisible(newVisible);
        localStorage.setItem("sidebarVisible", JSON.stringify(newVisible));
      }}
      sx={{
        position: "absolute",
        left: 0,
        top: 60,
        zIndex: 1001, // Increased from 1000 to be above the headbar
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <ChevronRightIcon />
    </IconButton>
  );

  const handleToggleCategoryVisibility = (category, event) => {
    event.stopPropagation(); // Prevent ListItemButton click
    const newHiddenCategories = hiddenCategories.includes(category)
      ? hiddenCategories.filter((c) => c !== category)
      : [...hiddenCategories, category];

    setHiddenCategories(newHiddenCategories);
    localStorage.setItem(
      "hiddenCategories",
      JSON.stringify(newHiddenCategories)
    );

    // If we're hiding the currently selected category, switch to 'all'
    if (newHiddenCategories.includes(selectedCategory)) {
      setSelectedCategory("all");
    }
  };

  const renderLeftPanel = () => {
    const queries = getQueriesForCategory(selectedCategory);

    // 4. Update the passesAllFilters function
    const passesAllFilters = (link) => {
      // Check confidence range
      const confidence = link.confidence || 0;
      if (confidence < confidenceRange[0] || confidence > confidenceRange[1]) {
        return false;
      }

      // Apply behavior filtering using centralized function
      return applyBehaviorFilter(link);
    };

    // Calculate total links for current category with all filters
    const totalQueryLinks = bidLinks.filter((link) => {
      // First apply date filter
      if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
        return false;
      }

      // Then apply category filter
      if (
        selectedCategory !== "all" &&
        getLinkDisplayCategory(link, teamCategories) !== selectedCategory
      ) {
        return false;
      }

      // Finally apply all other filters
      return passesAllFilters(link);
    }).length;

    return (
      <Card
        sx={{
          width: 250,
          height: "calc(100vh - 80px)",
          position: "sticky",
          top: 20,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              pb: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" style={{ fontWeight: "normal" }}>
                {viewMode === "categories" ? "Categories" : "Queries"}
              </Typography>
              {viewMode === "queries" && (
                <Button
                  size="small"
                  onClick={() => {
                    setViewMode("categories");
                    setSelectedQueries([]);
                  }}
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
              )}
            </Box>
          </Box>

          <List
            sx={{
              flexGrow: 1,
              overflow: "auto",
            }}
          >
            {viewMode === "categories" ? (
              <>
                <ListItemButton
                  selected={selectedCategory === "all"}
                  onClick={() => setSelectedCategory("all")}
                  sx={{ pr: 8 }}
                >
                  <ListItemText
                    primary="All"
                    secondary={`${
                      bidLinks.filter((link) => {
                        // Apply date filter
                        if (!matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange)) {
                          return false;
                        }

                        // Apply category visibility filter
                        if (hiddenCategories.includes(getLinkDisplayCategory(link, teamCategories))) {
                          return false;
                        }

                        // Apply all other filters
                        return passesAllFilters(link);
                      }).length
                    } links`}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory("all");
                      setViewMode("queries");
                    }}
                    sx={{ position: "absolute", right: 8 }}
                  >
                    <SegmentIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
                {categories
                  .filter((category) => !hiddenCategories.includes(category))
                  .map((category) => {
                    const categoryCount = bidLinks.filter(
                      (link) =>
                        getLinkDisplayCategory(link, teamCategories) === category &&
                        matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange) &&
                        passesAllFilters(link)
                    ).length;

                    // Hide category if count is 0
                    if (categoryCount === 0) return null;

                    return (
                      <ListItemButton
                        key={category}
                        selected={selectedCategory === category}
                        onClick={() => setSelectedCategory(category)}
                        sx={{ pr: 16 }}
                      >
                        <ListItemText
                          primary={category}
                          secondary={`${categoryCount} links`}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleToggleCategoryVisibility(category, e)
                          }
                          sx={{ position: "absolute", right: 48 }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCategory(category);
                            setViewMode("queries");
                          }}
                          sx={{ position: "absolute", right: 8 }}
                        >
                          <SegmentIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    );
                  })}
                {/* For hidden categories section, update the visibility icon position */}
                {hiddenCategories.length > 0 && (
                  <>
                    <ListItemText
                      primary="Hidden Categories"
                      sx={{ px: 2, pt: 2, pb: 1, color: "text.secondary" }}
                    />
                    {categories
                      .filter((category) => hiddenCategories.includes(category))
                      .map((category) => (
                        <ListItemButton
                          key={category}
                          sx={{ pr: 16, opacity: 0.6 }}
                        >
                          <ListItemText primary={category} />
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              handleToggleCategoryVisibility(category, e)
                            }
                            sx={{ position: "absolute", right: 48 }}
                          >
                            <VisibilityOffIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCategory(category);
                              setViewMode("queries");
                            }}
                            sx={{ position: "absolute", right: 8 }}
                          >
                            <SegmentIcon fontSize="small" />
                          </IconButton>
                        </ListItemButton>
                      ))}
                  </>
                )}
              </>
            ) : (
              <>
                <ListItemButton
                  selected={selectedQueries.length === 0}
                  onClick={() => {
                    setSelectedQueries([]);
                    setPage(0);
                  }}
                >
                  <ListItemText
                    primary="All Queries"
                    secondary={`${totalQueryLinks} links`}
                  />
                </ListItemButton>
                {queries.map((query) => {
                  const queryCount = bidLinks.filter(
                    (link) =>
                      link.queryId?.link === query &&
                      matchesPostedDateTimeRanges(link, dateTimeFilter.timeRange) &&
                      passesAllFilters(link)
                  ).length;

                  return (
                    <ListItemButton
                      key={query}
                      selected={selectedQueries.includes(query)}
                      onClick={() => {
                        setSelectedQueries([query]);
                        setPage(0);
                      }}
                    >
                      <ListItemText
                        primary={query}
                        secondary={`${queryCount} links`}
                      />
                    </ListItemButton>
                  );
                })}
              </>
            )}
          </List>
        </CardContent>
      </Card>
    );
  };

  const extractEmails = () => {
    // Use filteredBidLinks instead of bidLinks to respect current filters
    const emails = filteredBidLinks
      .filter(
        (link) => link.final_details?.applicationMethods?.applicationEmail
      )
      .map((link) => link.final_details.applicationMethods.applicationEmail);

    if (emails.length === 0) {
      toast.info("No emails found in the filtered bid links");
      return;
    }

    // Join emails with commas
    const emailList = emails.join(", ");

    // Copy to clipboard
    navigator.clipboard.writeText(emailList);
    toast.success(`Copied ${emails.length} emails to clipboard`);
  };

  const handleOpenAllLinks = () => {
    const visibleLinks = filteredBidLinks
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((link) => link.final_details?.finalUrl || link.url)
      .map(stripTrackingParams)
      .filter(Boolean);

    // Save opened links to localStorage and update state
    const updatedLinks = addOpenedLinks(visibleLinks);
    setOpenedLinks(updatedLinks);

    visibleLinks.forEach((url) => {
      openExternalUrl(url);
    });

    toast.info(`Opened ${visibleLinks.length} links`);
  };

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      {sidebarVisible && renderSidebar()}
      <Box sx={{ flex: 1, position: "relative" }}>
        {!sidebarVisible && renderSidebarToggle()}

        {/* Headbar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 2,
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {/* Search Box */}
          <TextField
            size="small"
            onKeyDown={async (e) => {
              if (e.key === "Enter" || (e.ctrlKey && e.key === "Enter")) {
                await handleGlobalSearch(e.target.value);
                e.target.value = "";
              }
            }}
            placeholder="Enter Search Term..."
            sx={{ minWidth: "150px", maxWidth: "200px", flex: 1, borderRadius: 2 }}
            variant="standard"
            InputProps={{
              endAdornment: isSearchInputLoading && (
                <CircularProgress size={20} />
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={handleOpenAllLinks}
            size="small"
            startIcon={<VisibilityIcon />}
          >
            Open All (
            {Math.min(
              rowsPerPage,
              filteredBidLinks.length - page * rowsPerPage
            )}
            )
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              const allLinks = filteredBidLinks
                .map((link) => link.final_details?.finalUrl || link.url)
                .map(stripTrackingParams)
                .filter(Boolean);
              navigator.clipboard.writeText(allLinks.join("\n"));
              toast.success(`Copied ${allLinks.length} links to clipboard`);
            }}
            size="small"
            startIcon={<ContentCopyIcon />}
          >
            Copy All ({filteredBidLinks.length})
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setOpenBlacklistDialog(true)}
            startIcon={<DoNotTouchIcon />}
            size="small"
          >
            Blacklists
          </Button>

          {(user?.role === "lead" || user?.role === "boss") && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                />
              }
              label="Hidden"
              sx={{ ml: 0 }}
            />
          )}
        </Box>

        {/* Add top margin to content to account for absolute positioned headbar */}
        <Box sx={{ mt: 10 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {isReloadingBids ? (
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", p: 4 }}
                >
                  <CircularProgress />
                </Grid>
              ) : (
                <FilteredBidLinks
                  filteredBidLinks={filteredBidLinks}
                  users={users}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {user.role !== 'bidder' && renderBlacklistPanel()}

        <DetailDialog
          open={Boolean(selectedLink)}
          onClose={() => setSelectedLink(null)}
          link={selectedLink}
          getUserNameById={getUserNameById}
          getRelativeTimeString={getRelativeTimeString}
          formatQueryDateLimit={formatQueryDateLimit}
          canEdit={user?.role !== 'bidder'}
          techStackOptions={techStackOptions.filter((o) => o.value !== 'all')}
          onLinkUpdated={(updatedLink) => {
            setSelectedLink(updatedLink);
            setBidLinks((prev) =>
              prev.map((l) => (l._id === updatedLink._id ? { ...l, ...updatedLink } : l))
            );
          }}
        />

        <Dialog
          open={openSearchDialog}
          onClose={() => setOpenSearchDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Global Search Results ({globalSearchResults.length})
          </DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table>
                <TableBody>
                  {globalSearchResults
                    .slice(
                      searchResultsPage * searchResultsRowsPerPage,
                      searchResultsPage * searchResultsRowsPerPage +
                        searchResultsRowsPerPage
                    )
                    .map((link) => (
                      <TableRow key={link._id} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Link
                                href={stripTrackingParams(link.final_details?.finalUrl || link.url)}
                                target="_blank"
                                rel={EXTERNAL_LINK_REL}
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "none",
                                  fontSize: "1.1rem",
                                  fontFamily:
                                    '"Inter","Roboto","Helvetica","Arial",sans-serif',
                                  fontWeight: 500,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                  "&:visited": {
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "#e0b0ff"
                                        : "#551A8B",
                                  },
                                }}
                              >
                                {link.title}
                                {link.confidence && (
                                  <ConfidenceIndicator
                                    confidence={link.confidence}
                                  />
                                )}
                              </Link>
                              <Chip
                                label={getLinkLocation(link)}
                                size="small"
                                sx={{
                                  ...getLocationChipColor(getLinkLocation(link)).sx,
                                }}
                              />
                              {link.final_details?.tag && (
                                <Chip
                                  label={link.final_details.tag}
                                  size="small"
                                  sx={{
                                    ...getTagColor(link.final_details.tag).sx,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              <Box component="span" sx={getPostedDateTextSx(link)}>
                                {formatLinkPostedDate(link.date, getRelativeTimeString) || "N/A"}
                              </Box>
                              {" - "}
                              {link.company || "N/A"}
                              {" "}
                              | Added:{" "}
                              {getRelativeTimeString(new Date(link.created_at))}
                              {link.created_by && users[link.created_by] && (
                                <> | By: {users[link.created_by]}</>
                              )}
                            </Typography>
                            <Typography variant="body2">
                              {link.description}
                            </Typography>
                            {renderLinkTechStacks(link)}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ width: "60px" }}>
                          <Tooltip title="Show Details" placement="left">
                            <Button
                              size="small"
                              onClick={() => setSelectedLink(link)}
                              sx={{
                                minWidth: "40px",
                                height: "40px",
                                borderRadius: 1,
                              }}
                            >
                              <InfoIcon color="action" />
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={globalSearchResults.length}
              page={searchResultsPage}
              onPageChange={(event, newPage) => setSearchResultsPage(newPage)}
              rowsPerPage={searchResultsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setSearchResultsRowsPerPage(parseInt(event.target.value, 10));
                setSearchResultsPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSearchDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default BidLinks;
